/**
 * Who stood behind a lab result, and what it said before it was amended (PROD-3275, DF-05).
 *
 * Pure text and matching rules for the chart Labs tab: the report provenance line ("AI-extracted ·
 * confirmed by …"), the amended suffix, and the per-analyte version history behind the history
 * popover. The wire carries user ids (`confirmedBy`, `createdBy`); the names are resolved by the
 * caller and passed in, so a raw id is never shown. The API has NO amend-reason field, so no reason
 * is ever shown.
 */
import { clinicDateTimeD45 } from '@/shared/lib/clinic-time';
import { asUtcInstant } from '@/shared/lib/utc-instant';
import type { LabObservation, LabReport } from '@/types/lab-results-api';

const lower = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/** A results-API timestamp (zone-less UTC) as the clinic's D-45 date and time, "13 Oct 2026, 10:12". */
export function labStampText(iso: string | null | undefined): string | undefined {
  return clinicDateTimeD45(asUtcInstant(iso));
}

// ─── Report provenance line (C1, C2) ────────────────────────────────────────

export interface LabReportProvenance {
  extracted: boolean;
  amended: boolean;
  version: number;
  /** The user who confirmed the extracted rows, and when (UTC). */
  confirmedBy: string | null;
  confirmedAt: string | null;
  /** When the current version was written: the "amended" time. */
  amendedAt: string | null;
}

export function reportProvenance(r: LabReport, amended: boolean): LabReportProvenance {
  return {
    extracted: lower(r.source) === 'extracted',
    amended,
    version: r.version ?? 1,
    confirmedBy: r.confirmedBy ?? null,
    confirmedAt: r.confirmedAt ?? null,
    amendedAt: r.createdDate ?? null,
  };
}

/**
 * "AI-extracted · confirmed by <name>, <date>", with " · amended <date> (version N)" for an amended
 * report. A report that was not extracted has no line (null). With no resolved name the line reads
 * "AI-extracted · confirmed <date>": never a raw id.
 */
export function labProvenanceLine(p: LabReportProvenance, name: string | null | undefined): string | null {
  if (!p.extracted) return null;
  const when = labStampText(p.confirmedAt);
  const who = name?.trim();
  const confirmed = who ? `confirmed by ${who}${when ? `, ${when}` : ''}` : `confirmed${when ? ` ${when}` : ''}`;
  let line = `AI-extracted · ${confirmed}`;
  if (p.amended) {
    const at = labStampText(p.amendedAt);
    line += ` · amended${at ? ` ${at}` : ''} (version ${p.version})`;
  }
  return line;
}

// ─── Analyte history across report versions (C2) ────────────────────────────

export interface LabVersionActor {
  /** amended: a later version; extracted: version 1 confirmed from a PDF; recorded: version 1 typed in. */
  kind: 'amended' | 'extracted' | 'recorded';
  userId: string | null;
  at: string | null;
}

export interface LabAnalyteVersion {
  version: number;
  /** The current version of the report. */
  current: boolean;
  value: string;
  unit: string;
  actor: LabVersionActor;
}

function analyteCode(o: LabObservation): string {
  return lower(o.loinc) || lower(o.scripsComponentCode) || lower(o.localCode);
}

/** The same analyte across versions: its code plus its display. */
export function sameAnalyte(a: LabObservation, b: LabObservation): boolean {
  return analyteCode(a) === analyteCode(b) && lower(a.display) === lower(b.display);
}

function actorOf(r: LabReport): LabVersionActor {
  const version = r.version ?? 1;
  if (version > 1) return { kind: 'amended', userId: r.createdBy ?? null, at: r.createdDate ?? null };
  if (lower(r.source) === 'extracted') return { kind: 'extracted', userId: r.confirmedBy ?? null, at: r.confirmedAt ?? null };
  return { kind: 'recorded', userId: r.createdBy ?? null, at: r.createdDate ?? null };
}

/**
 * The versions of one analyte, newest first, or null when its value or unit never changed (or there
 * is only one version). `all` is every version the read returned (`includeHistory=true`); versions
 * of the same report share `rootLabReportId`.
 */
export function buildAnalyteVersions(
  current: LabObservation,
  report: LabReport,
  all: LabReport[],
  formatValue: (o: LabObservation) => string,
): LabAnalyteVersion[] | null {
  const root = report.rootLabReportId ?? report.id;
  if (!root) return null;
  const versions: LabAnalyteVersion[] = [];
  const siblings = all
    .filter((r) => (r.rootLabReportId ?? r.id) === root)
    .sort((a, b) => (b.version ?? 1) - (a.version ?? 1));
  for (const r of siblings) {
    const o = (r.observations ?? []).find((x) => sameAnalyte(x, current));
    if (!o) continue;
    versions.push({
      version: r.version ?? 1,
      current: r.id === report.id,
      value: formatValue(o),
      unit: o.unit?.trim() || o.unitText?.trim() || '',
      actor: actorOf(r),
    });
  }
  if (versions.length < 2) return null;
  const changed = versions.some((v) => v.value !== versions[0].value || lower(v.unit) !== lower(versions[0].unit));
  return changed ? versions : null;
}

/** "Amended by <name> · <date>" / "AI-extracted, confirmed by <name> · <date>" / "Recorded by <name> · <date>". */
export function versionActorText(actor: LabVersionActor, name: string | null | undefined): string {
  const who = name?.trim();
  const when = labStampText(actor.at);
  const lead =
    actor.kind === 'amended'
      ? who ? `Amended by ${who}` : 'Amended'
      : actor.kind === 'extracted'
        ? who ? `AI-extracted, confirmed by ${who}` : 'AI-extracted, confirmed'
        : who ? `Recorded by ${who}` : 'Recorded';
  return when ? `${lead} · ${when}` : lead;
}
